import { Trans, useLingui } from "@lingui/react/macro";

import { useHeroLookup, useRankLookup } from "@/features/matches";
import { isLocale } from "@/shared/i18n";

import type { DashboardHero, DashboardLocale, DashboardRank } from "../../model/TeamDashboard";
import { BarList } from "./BarList";
import { DashboardCard } from "./DashboardCard";

type WhoPlayersProps = {
  ranks: DashboardRank[];
  heroes: DashboardHero[];
  locales: DashboardLocale[];
};

// Название языка на нём самом («Русский», «English») — его не переводят.
const nativeName = (locale: string): string => {
  const tag = isLocale(locale) ? locale : "en";
  const name = new Intl.DisplayNames([tag], { type: "language" }).of(locale) ?? locale;
  return name.charAt(0).toLocaleUpperCase(tag) + name.slice(1);
};

// «Кто наши игроки»: ранги и язык активных за период, герои разобранных матчей.
export const WhoPlayers = ({ ranks, heroes, locales }: WhoPlayersProps) => {
  const { t } = useLingui();
  const rank = useRankLookup();
  const hero = useHeroLookup();
  const columns = [
    {
      key: "ranks",
      title: <Trans>Rank</Trans>,
      rows: ranks.map((row) => ({
        key: row.tier === null ? "none" : String(row.tier),
        label: row.tier === null ? t`No rank` : rank(row.tier * 10).name,
        value: row.players,
      })),
    },
    {
      key: "heroes",
      title: <Trans>Heroes in analyzed matches</Trans>,
      rows: heroes.map((row) => ({
        key: String(row.hero_id),
        label: hero(row.hero_id).name,
        value: row.analyses,
      })),
    },
    {
      key: "locales",
      title: <Trans>Site language</Trans>,
      rows: locales.map((row) => ({
        key: row.locale,
        label: nativeName(row.locale),
        value: row.players,
      })),
    },
  ];

  return (
    <DashboardCard
      title={<Trans>Who our players are</Trans>}
      description={<Trans>Players who used Relock in the period</Trans>}
    >
      <div className="grid grid-cols-[repeat(auto-fit,minmax(min(260px,100%),1fr))] gap-x-7 gap-y-4">
        {columns.map((column) => (
          <div key={column.key} className="flex flex-col gap-2.5">
            <h3 className="text-[13px] font-semibold text-muted">{column.title}</h3>
            {column.rows.length === 0 ? (
              <p className="text-[13px] text-subtle">—</p>
            ) : (
              <BarList rows={column.rows} data-list={column.key} />
            )}
          </div>
        ))}
      </div>
    </DashboardCard>
  );
};
